<template>
  <ConfigurationCardSkeleton v-if="!cardData" />
  <ConfigurationCard v-else>
    <template
      v-if="hasImage"
      #default
    >
      <img
        v-if="cardData?.diagram_thumbnail_url"
        :src="cardData.diagram_thumbnail_url"
      >
      <template v-else>
        <div class="issue-card__image-waiting" />
      </template>
    </template>
    <template #details>
      <p class="typo-ignore typo-small-title typo-bold">
        {{ cardData.name }}
      </p>
      <p class="set-text typo-ignore typo-tiny">
        {{ cardData?.setText }}
      </p>
    </template>
    <template #actions>
      <div :class="{ 'actions': hasMoreActions, 'actions--min-width': hasTwoActions }">
        <!-- TODO: future feature to be added for preview -->
        <div
          v-if="actionData?.preview"
          class="actions__icon show-for-medium"
        >
          <PreviewIcon />
        </div>
        <router-link
          v-if="actionData?.regions"
          class="actions__icon show-for-medium"
          :to="actionData.regions"
        >
          <BoundsIcon />
        </router-link>
        <router-link
          v-if="actionData?.edit"
          class="actions__icon show-for-medium"
          :to="actionData.edit"
        >
          <PencilIcon />
        </router-link>
      </div>
    </template>
    <template #menu>
      <Dropdown
        :items="setDropdownList(cardData)"
        @click-item="onItemAction($event)"
      >
        <UseSvg
          class="icon"
          name="vertical_ellipsis"
        />
      </Dropdown>
    </template>
  </ConfigurationCard>

  <Dialog
    v-if="dialogIsOpen"
    :message="I18n.t('issues.confirm_delete_message')"
    :title="
      I18n.t('issues.confirm_delete_title', {
        type: I18n.t(`operation_type.${operationType}`),
      })
    "
    @dismiss-dialog="dismissDialogAction"
  >
    <template #actions>
      <button
        aria-label="delete"
        class="button button--inverse"
        type="button"
        @click="onDialogAction"
      >
        {{ $t('general.yes') }}
      </button>
    </template>
  </Dialog>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';

// Utils
import I18n from 'i18n-js';
import useAjax from '@/functions/use_ajax';
import isDesktop from '@/functions/responsive';
import { useRouter } from 'vue-router';

// Components
import ConfigurationCard from '@/components/generic/configuration_card.vue';
import ConfigurationCardSkeleton from '@/components/organisations/configuration/configuration_card_skeleton.vue';
import Dialog from '@/components/generic/dialog.vue';
import Dropdown from '@/components/generic/dropdown.vue';

// Icons
import PreviewIcon from '@/images/preview-image.svg';
import BoundsIcon from '@/images/define-bounds-20.svg';
import PencilIcon from '@/images/pencil-13.svg';
import UseSvg from '@/components/generic/use_svg.vue';

const emit = defineEmits(['deleteItem']);

const props = defineProps({
  organisationId: { type: String, required: true, default: null },
  endpointIdentifier: { type: String, required: true, default: 'stations' },
  operationType: { type: String, required: false, default: 'station' },
  listItem: { type: Object, default: null },
});

const router = useRouter();
const ajax = useAjax();
const cardData = ref(null);
const dialogIsOpen = ref(false);
const dialogAction = ref(null);

const endpoint = computed(() => {
  switch (true) {
    case props.endpointIdentifier === 'plants/heavy_mobile_equipments':
      return `/organisations/${props.organisationId}/operations/${props.listItem.operation_id}/${props.endpointIdentifier}/${props.listItem?.id}`;
    case ['mines', 'equipment_processes', 'equipment_types', 'plant_types'].includes(props.endpointIdentifier):
      return `/organisations/${props.organisationId}/${props.endpointIdentifier}/${props.listItem?.id}`;
    default:
      return `/organisations/${props.organisationId}/shafts_v2/shafts/${props.listItem.shaft_id}/${props.endpointIdentifier}/${props.listItem?.id}`;
  }
});

const actionData = computed(() => {
  switch (true) {
    case props.endpointIdentifier === 'heavy_mobile_equipments':
      return {
        regions: {
          name: 'shaftV2ManageRegionsHme',
          params: { heavyMobileEquipmentId: cardData.value?.id },
        },
        edit: {
          name: 'editShaftV2Hme',
          params: { heavyMobileEquipmentId: cardData.value?.id },
        },
      };
    case props.endpointIdentifier === 'plants/heavy_mobile_equipments':
      return {
        regions: {
          name: 'manageRegionsHme',
          params: { heavyMobileEquipmentId: cardData.value?.id },
        },
        edit: {
          name: 'editPlantsHme',
          params: { heavyMobileEquipmentId: cardData.value?.id },
        },
      };
    case props.endpointIdentifier === 'mines':
      return {
        edit: {
          name: 'mineForm',
          params: { mineId: cardData.value?.id },
        },
      };
    case props.endpointIdentifier === 'equipment_processes':
      return {
        edit: {
          name: 'equipmentProcessForm',
          params: { processId: cardData.value?.id },
        },
      };
    case props.endpointIdentifier === 'equipment_types':
      return {
        edit: {
          name: 'equipmentTypeForm',
          params: { typeId: cardData.value?.id },
        },
      };
    case props.endpointIdentifier === 'plant_types':
      return {
        edit: {
          name: 'operationTypeForm',
          params: { typeId: cardData.value?.id },
        },
      };
    default:
      return {
        regions: {
          name: 'shaftV2ManageRegions',
          params: { stationId: cardData.value?.id },
        },
        edit: {
          name: 'shaftV2StationForm',
          params: { stationId: cardData.value?.id },
        },
      };
  }
});

const hasImage = computed(() => {
  return cardData.value && Object.prototype.hasOwnProperty.call(cardData.value, 'diagram_thumbnail_url');
});

const hasMoreActions = computed(() => {
  return actionData.value && Object.keys(actionData.value).length > 1;
});

const hasTwoActions = computed(() => {
  return actionData.value && Object.keys(actionData.value).length === 2;
});

onMounted(async () => {
  loadItem();
});

onUnmounted(() => {
  ajax.abort();
});

async function loadItem() {
  let { ok, data } = await ajax(endpoint.value);

  if (ok) {
    cardData.value = data;
    const hasSetData = data?.start_set && data?.end_set;
    hasSetData && (cardData.value.setText = `${I18n.t('shafts.issues.set')} ${data.start_set}-${data.end_set}`);
  }
}

async function deleteItem() {
  let { ok, status } = await ajax(endpoint.value, { method: 'DELETE' });

  if (ok || status === 303) {
    emit('deleteItem', props.listItem?.id);
  }
}

/**
 * Toggles dialog,
 * stores the id used for deletion,
 * stores the key form the dropdown menu item
 */
function onItemAction({ key }) {
  if (key === 'delete') {
    dialogIsOpen.value = true;
    dialogAction.value = key;
  } else {
    router.replace(actionData.value[key]);
  }
}

/**
 * Closes and clears any stored values
 */
function dismissDialogAction() {
  dialogIsOpen.value = false;
  dialogAction.value = null;
}

async function onDialogAction() {
  deleteItem();
  dismissDialogAction();
}

/**
 * Sets the item route data
 */
function setDropdownList(station) {
  /**
   * TODO: add modifier when policies get added
   * !station?.policy?.destroy ? 'disabled' : null
   */
  const list = [
    {
      label: I18n.t('general.edit'),
      key: 'edit',
    }
  ];
  const mobileList = !isDesktop() && list || [];
  return [
    ...mobileList,
    {
      label: I18n.t('general.delete'),
      key: 'delete',
      modifiers: ['destructive', null],
    },
  ];
}
</script>
<style scoped lang="scss">
@import '@/stylesheets/global';

img {
  max-width: unset;
  height: 100%;
}

.set-text {
  color: map-get($foreground-color, dark);
}

.actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: bu(15);

  &--min-width {
    min-width: bu(9);
  }

  &__pencil {
    padding-top: bu(0.5);
  }

  &__icon {
    cursor: pointer;
  }
}

.configuration-card {
  @include breakpoint(small) {
    .dropdown {
      padding: bu(2) 0 bu(1) bu(3);
    }

    .typo-bold {
      font-weight: 500;
    }
  }

  @include breakpoint(medium) {
    .dropdown {
      padding: 0 0 0 bu(3);
      visibility: hidden;
    }

    &:hover {
      .dropdown {
        visibility: visible;
      }
    }

    .typo-bold {
      font-weight: inherit;
    }
  }
}
</style>
